import CrossDomainHttpClient from '@/services/crossDomainHttp'
import Configs from '@/config'

const DEFAULT_POLL_INTERVAL_MS = 2000
const DEFAULT_MAX_ATTEMPTS = 600

const { externalApis } = Configs

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms) })

const pollLoop = async ({
  statusUrl,
  authHeaders,
  isCancelled,
  pollIntervalMs,
  maxAttempts,
  parse,
}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (isCancelled?.()) {
      return null
    }
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(pollIntervalMs)
    }
    if (isCancelled?.()) {
      return null
    }
    // eslint-disable-next-line no-await-in-loop
    const resp = await CrossDomainHttpClient.get(statusUrl, {}, authHeaders)
    if (isCancelled?.()) {
      return null
    }
    const outcome = parse(resp)
    if (outcome) {
      return outcome
    }
  }
  const timeoutSeconds = (maxAttempts * pollIntervalMs) / 1000
  throw new Error(`Polling ${statusUrl} timed out after ${timeoutSeconds}s.`)
}

/**
 * Resolve once the cloud-imported file's background download has finished.
 * Throws on failure or timeout. `onMeta` (optional) fires on each tick with
 * the file metadata so the caller can mirror progress into local state.
 */
export const waitForCloudImportReady = async (
  hashId,
  {
    authHeaders = {},
    isCancelled,
    onMeta,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
  } = {},
) => {
  const statusUrl = `${externalApis.pptPdfUploadUsingDocumentSystem}/${hashId}`
  return pollLoop({
    statusUrl,
    authHeaders,
    isCancelled,
    pollIntervalMs,
    maxAttempts,
    parse: (resp) => {
      const meta = resp.body?.metadata
      onMeta?.(meta)
      const status = meta?.import_status
      if (status === 'success') {
        return { meta }
      }
      if (status === 'failed') {
        throw new Error(meta?.import_error || 'Cloud import failed.')
      }
      return null
    },
  })
}

/**
 * Resolve once an import job reaches a terminal state. Throws on failure or
 * timeout. Returns the final job body on success.
 */
export const waitForImportJob = async (
  jobId,
  {
    authHeaders = {},
    isCancelled,
    onProgress,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
  } = {},
) => {
  const statusUrl = `${externalApis.pptPdfImportJobStatus}/${jobId}`
  return pollLoop({
    statusUrl,
    authHeaders,
    isCancelled,
    pollIntervalMs,
    maxAttempts,
    parse: (resp) => {
      const { body } = resp
      const status = body?.status
      // `result` carries the import's sub-status while `status` is still
      // 'processing': phase, slide counts and caption counts. It is how the
      // caller learns the deck is usable (result.slides_ready) long before the
      // job reaches a terminal state — under the v2 pipeline captioning runs
      // after the slides exist, so 'success' lands well after they are visible.
      //
      // Deliberately not a new `status` value: this loop resolves on 'success',
      // throws on 'failed' and returns null for anything else, so an unknown
      // status would poll to maxAttempts and then throw on a healthy import.
      onProgress?.(body?.result, body)
      if (status === 'success') {
        return body
      }
      if (status === 'failed') {
        const msg = body?.error_message || 'Unknown error'
        throw new Error(`Import job ${jobId} failed: ${msg}`)
      }
      return null
    },
  })
}
